import { useTranslations } from 'next-intl';
import Counter from '../Counter';
import { Button } from '@/components/ui/button';
import NumberToPrice from '@/_components/NumberToPrice';
import useGTM from '@/_hooks/useGTM';
import { track } from '@/_utils/utm';
import { useTelegram } from '@/_providers/telegram';
import { BRAND_NAMES } from '@/_utils/Constants';
import { ADD_TO_CART, REMOVE_PRODUCTS_BY_OPPOSITE_RESTRICTION } from '@/_store/cart/cart.slice';
import { onOpenMap } from '@/_store/common/common.slice';
import { IRedux } from '@/_store/types';
import { toaster } from '@/components/ui/toaster';
import { Box, Flex, HStack, Text } from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import ConfirmModal from '@/_components/ConfirmModal';
import { needsDestinationSelection } from '@/_lib/destination-gate';
import { IFooterProps } from './footer';
import { ICategory } from '@/_types';

export default function FooterDesktop({
    data,
    branch,
    shipperId,
    activeVariant,
    totalDiscount,
    price,
    modifiersPrice,
    quantity,
    productInCart,
    orderModifiers,
    modifiersQuantity,
    compulsories,
    setStartTracking,
    onIncrease,
    onDecrease,
    onRedirectToCart,
    backToBrand,
    expectedModifier,
}: IFooterProps) {
    const t = useTranslations();
    const dispatch = useDispatch();
    const { sendGTM } = useGTM();
    const { tg_chat_id } = useTelegram();
    const currentLocale = useCustomLocale();

    const country = useSelector((state: IRedux) => state.settings?.country);
    const { permissions, is_hall, order_type } = useSelector(
        (state: IRedux) => state.session,
    );
    const cartProducts = useSelector(
        (state: IRedux) => state.cart.cart?.[shipperId]?.products ?? []
    );
    const [showRestrictedConfirm, setShowRestrictedConfirm] = useState(false);
    const category = data?.categories?.[0] as ICategory | undefined;
    const newProductRestricted = category?.is_shopping_cart_restricted ?? false;
    const hasOppositeCategoryInCart = useMemo(
        () =>
            cartProducts.some(
                (p) => (p.category_is_restricted ?? false) !== newProductRestricted
            ),
        [cartProducts, newProductRestricted]
    );

    const currentBrandName = BRAND_NAMES?.[shipperId]

    const addToCartGTM = useCallback(() => {
        if (typeof window !== 'undefined' && window.dataLayer) {
            window?.dataLayer?.push({ ecommerce: null });
            sendGTM('add_to_cart', { product: data, quantity });
            window?.dataLayer?.push({ items: null, content_ids: null });
            sendGTM('add_to_cart_ads', { product: data });
            sendGTM('add_to_cart_fb', { product: data, currency: country?.currency });
        } else {
            console.warn('GTM dataLayer is not available.');
        }
    }, [data, quantity, currentLocale]);

    const addToCartHandler = () => {
        if (data?.id) {
            if (data?.type == 'origin') {
                if (activeVariant) {
                    dispatch(
                        ADD_TO_CART({
                            image: data?.image || '',
                            brand: currentBrandName,
                            key: '',
                            comment: '',
                            in_stop: false,
                            default_product: false,
                            discount_percent:
                                totalDiscount !== 0
                                    ? Math.floor((totalDiscount / activeVariant.out_price) * 100)
                                    : 0,
                            product_name: data.title?.[currentLocale],
                            category_name: category?.title?.[currentLocale],
                            category_id: category?.id,
                            category_is_restricted: category?.is_shopping_cart_restricted,
                            price_with_discount: activeVariant.out_price + totalDiscount,
                            price: activeVariant?.out_price,
                            product_id: activeVariant?.id,
                            type: 'variant',
                            variants: {},
                            order_modifiers: orderModifiers,
                            quantity: quantity,

                            shipper_id: shipperId,
                            source_id: '',
                            branch_id: branch?.id || '',
                            menu_id: branch?.menu_id || '',
                        }),
                    );
                    track({
                        action_type: 'add_to_cart',
                        tg_chat_id,
                    });
                    sendGTM('add_to_cart', { product: activeVariant, quantity });
                } else {
                    toaster.create({
                        title: t('choose_one_options'),
                        type: 'warning',
                    });
                }
            } else {
                dispatch(
                    ADD_TO_CART({
                        image: data?.image || '',
                        brand: currentBrandName,
                        key: '',
                        comment: '',
                        in_stop: false,
                        default_product: false,
                        discount_percent:
                            totalDiscount !== 0
                                ? Math.floor((totalDiscount / price) * 100)
                                : 0,
                        product_name: data.title?.[currentLocale],
                        category_name: category?.title?.[currentLocale],
                        category_id: category?.id,
                        category_is_restricted: category?.is_shopping_cart_restricted,
                        price: price,
                        price_with_discount: price + totalDiscount,
                        product_id: data?.id,
                        type: data?.type,
                        variants: {},
                        order_modifiers: orderModifiers,
                        quantity: quantity,

                        shipper_id: shipperId,
                        source_id: '',
                        branch_id: branch?.id || '',
                        menu_id: branch?.menu_id || '',
                    }),
                );
                track({
                    action_type: 'add_to_cart',
                    tg_chat_id,
                });
                addToCartGTM();
            }
        }
    };

    const onConfirmReplaceCart = useCallback(() => {
        dispatch(
            REMOVE_PRODUCTS_BY_OPPOSITE_RESTRICTION({
                shipper_id: shipperId,
                is_restricted: newProductRestricted,
            })
        );
        setShowRestrictedConfirm(false);
        addToCartHandler();
    }, [dispatch, shipperId, newProductRestricted]);

    const onOrderClick = () => {
        if (
            needsDestinationSelection({
                isHall: is_hall,
                orderType: order_type?.type,
                branchId: branch?.id,
                geolocation: permissions.geolocation,
            })
        ) {
            dispatch(onOpenMap());
            return;
        }

        if (checkModifierRequired()) {
            if (hasOppositeCategoryInCart) {
                setShowRestrictedConfirm(true);
            } else {
                addToCartHandler();
            }
        } else {
            setStartTracking(true);
        }
    };
    // Get compulsory and not selected modifier
    const checkModifierRequired = () => {
        if (!compulsories || compulsories.length === 0) return true;

        for (const item of compulsories) {
            const hasParent = !!item.parent_id;
            const orderModifier = orderModifiers?.find((element) => {
                const parentMatches = hasParent
                    ? element.parent_id === item.parent_id
                    : !element.parent_id && element.modifier_id === item.modifier_id;

                const quantityValid = element.modifier_quantity > 0;

                const minAmountValid = hasParent
                    ? (modifiersQuantity.find((mod) => mod.id === element.parent_id)
                        ?.quantity || 0) >= (element.parent_min_amount || 0)
                    : true;

                return parentMatches && quantityValid && minAmountValid;
            });

            if (!orderModifier) return false;
        }
        return true;
    };

    return (
        <>
        <HStack
            position={{ base: 'sticky', lg: 'absolute' }}
            bottom={0}
            left={0}
            bg='background.card'
            justify={'space-between'}
            zIndex={10}
            roundedBottomRight={24}
            w='full'
            gap={{ base: 4, lg: 6 }}
            py={{ base: 4, lg: 3 }}
            px={{ base: 4, lg: 4 }}
            borderTop='1px solid'
            borderColor='#00000014'
            boxShadow={{
                base: '0px -4px 6px -2px rgba(0, 0, 0, 0.08)',
                lg: 'none',
            }}
        >
            <HStack align={{ base: 'center', lg: 'center' }} flex={1}>
                {totalDiscount !== 0 && (
                    <Box
                        textDecor={'line-through'}
                        fontSize={{ base: 'md', lg: 'sm' }}
                        color={'fg.error'}
                    >
                        <NumberToPrice
                            textProps={{
                                lineHeight: 1,
                                fontWeight: 600,
                            }}
                            spanProps={{
                                lineHeight: 1,
                                fontWeight: 600,
                            }}
                            hideCurrency
                            value={
                                productInCart?.quantity
                                    ? (price + modifiersPrice) * productInCart.quantity
                                    : (price + modifiersPrice) * quantity
                            }
                        />
                    </Box>
                )}
                <Text as='span'>
                    <NumberToPrice
                        textProps={{
                            fontWeight: 700,
                            fontSize: { base: 'lg', lg: '2xl' },
                        }}
                        spanProps={{
                            fontWeight: 700,
                            fontSize: { base: 'lg', lg: '2xl' },
                        }}
                        value={
                            productInCart?.quantity
                                ? (price + modifiersPrice + totalDiscount) *
                                productInCart.quantity
                                : (price + modifiersPrice + totalDiscount) * quantity
                        }
                    />
                </Text>
            </HStack>
            <Flex gap={4} flex={1}>
                <Counter
                    width={'130px'}
                    variable={quantity}
                    height={{ base: '52px', lg: '56px' }}
                    onIncrease={() => onIncrease()}
                    onDecrease={() => onDecrease()}
                    color='text'
                />
                {Boolean(productInCart) ? (
                    <Button
                        flex={1}
                        onClick={backToBrand}
                        bg='primary'
                        color='primary.text'
                        _hover={{
                            bg: 'primary.hover',
                        }}
                        rounded={16}
                        fontWeight={600}
                        display={{ base: 'none', lg: 'flex' }}
                        fontSize='lg'
                        height='56px'
                    >
                        {t('added_to_cart')}
                    </Button>
                ) : (
                    <Button
                        onClick={onOrderClick}
                        disabled={Boolean(
                            branch?.menu_id && !data?.active_in_menu,
                        )}
                        flex={1}
                        bg='primary'
                        color='primary.text'
                        _hover={{
                            bg: 'primary.hover',
                        }}
                        rounded={16}
                        fontWeight={600}
                        fontSize={{ base: 'sm', lg: 'lg' }}
                        height={{ base: '52px', lg: '56px' }}
                    // loading={data?.has_modifier && !orderModifiers.length}
                    >
                        {expectedModifier?.length > 0
                            ? `${t('select_smth', {
                                item:
                                    expectedModifier[0]?.length > 20
                                        ? expectedModifier[0]?.slice(0, 20) + '...'
                                        : expectedModifier[0],
                            })}`
                            : t('add_to_cart')}
                    </Button>
                )}
            </Flex>
        </HStack>
        <ConfirmModal
            isOpen={showRestrictedConfirm}
            onClose={() => setShowRestrictedConfirm(false)}
            onConfirm={onConfirmReplaceCart}
            title={t('attention')}
            description={t('cart_restricted_confirm_message')}
            text={{ confirm: 'yes', cancel: 'no' }}
        />
        </>
    );
}
